import type { BackgroundSetting, ThemeSetting } from '@neosocial/protocol';
import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { tr, useT } from '../i18n';
import { api } from '../lib/api';
import { desktopBridge, serverOrigin } from '../lib/config';
import { failToast } from '../mod/actions';
import { updateData, useData } from '../store/data';
import { chooseBackground, chooseTheme } from '../store/look';
import {
  glassPref,
  motionPref,
  setGlassPref,
  setMotionPref,
  shownSolid,
  useUi,
  type GlassPref,
  type MotionPref,
} from '../store/ui';
import { Btn, Icon, Seg, Switch } from '../ui/kit';
import { toast } from '../ui/overlay';
import markUrl from '../assets/mark.svg';
import { UpdateStatusLine } from '../shell/Update';
import { SetGroup, SetRow } from './Settings';

/** The theme is saved to the account; when that fails the device keeps it, and says so. */
function sayIfNotSaved(saved: boolean): void {
  if (!saved) toast(tr().t('set.look.notSaved'), { tone: 'warn', icon: 'alert' });
}

type Solid = Exclude<BackgroundSetting, 'glow'>;
/** The solid colours each theme offers (a choice shows as its pair in the other theme). */
const SOLIDS: Record<'dark' | 'light', Solid[]> = {
  dark: ['black', 'charcoal', 'midnight'],
  light: ['white', 'grey'],
};

const DARK_Q = '(prefers-color-scheme: dark)';
const systemDark = () => !!globalThis.matchMedia?.(DARK_Q).matches;
function watchSystemDark(cb: () => void): () => void {
  const m = globalThis.matchMedia?.(DARK_Q);
  m?.addEventListener?.('change', cb);
  return () => m?.removeEventListener?.('change', cb);
}

/** True while the dark theme shows (Smoked, or System on a dark system). */
function useDarkShown(theme: ThemeSetting): boolean {
  const sys = useSyncExternalStore(watchSystemDark, systemDark, () => false);
  return theme === 'smoked' || (theme === 'system' && sys);
}

/**
 * The background's colours: round swatches with their names, a radio group
 * (arrow keys move the choice, as in the kit's Seg).
 */
function SolidSwatches({
  options,
  value,
  onChange,
  label,
}: {
  options: Solid[];
  value: Solid | null;
  onChange(v: Solid): void;
  label: string;
}) {
  const { t } = useT();
  const k = Math.max(0, value ? options.indexOf(value) : 0);
  const move = (i: number, el: HTMLElement) => {
    const n = options.length;
    const o = options[(i + n) % n]!;
    onChange(o);
    el.parentElement?.querySelectorAll('button')[(i + n) % n]?.focus();
  };
  return (
    <div
      className="ns-bg-swatches"
      role="radiogroup"
      aria-label={label}
      onKeyDown={(e) => {
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          e.preventDefault();
          move(k + 1, e.target as HTMLElement);
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          e.preventDefault();
          move(k - 1, e.target as HTMLElement);
        }
      }}
    >
      {options.map((o, i) => (
        <button
          key={o}
          type="button"
          role="radio"
          className="ns-bg-sw"
          data-c={o}
          aria-checked={o === value}
          tabIndex={i === k ? 0 : -1}
          onClick={() => onChange(o)}
        >
          <i className="dot" aria-hidden="true" />
          <span>{t(`set.bg.${o}`)}</span>
        </button>
      ))}
    </div>
  );
}

export function AppearanceSection() {
  const { t } = useT();
  const theme = useUi((u) => u.theme);
  // the window behind the glass: the aurora or a solid colour (store/ui.ts sets data-bg)
  const background = useUi((u) => u.background);
  const dark = useDarkShown(theme);
  const shown = shownSolid(background, dark);
  const pickBackground = (v: BackgroundSetting) => void chooseBackground(v).then(sayIfNotSaved);
  // this device's choices (store/ui.ts sets data-motion and data-glass on <html>)
  const [motion, setMotion] = useState<MotionPref>(motionPref);
  const [glass, setGlass] = useState<GlassPref>(glassPref);
  return (
    <>
      <h2>{t('set.appearance')}</h2>
      <SetGroup>
        <SetRow label={t('set.theme')} sub={t('set.theme.sub')}>
          <Seg<ThemeSetting>
            label={t('set.theme')}
            value={theme}
            onChange={(v) => void chooseTheme(v).then(sayIfNotSaved)}
            options={[
              { value: 'pearl', label: t('set.theme.light'), icon: 'sun' },
              { value: 'smoked', label: t('set.theme.dark'), icon: 'moon' },
              { value: 'system', label: t('set.theme.system'), icon: 'monitor' },
            ]}
          />
        </SetRow>
        <SetRow label={t('set.bg')} sub={t('set.bg.sub')}>
          <Seg<'glow' | 'solid'>
            label={t('set.bg')}
            value={background === 'glow' ? 'glow' : 'solid'}
            onChange={(v) => {
              if (v === 'glow') pickBackground('glow');
              else if (background === 'glow') pickBackground(dark ? 'black' : 'white');
            }}
            options={[
              { value: 'glow', label: t('set.bg.glow'), icon: 'sparkles' },
              { value: 'solid', label: t('set.bg.solid'), icon: 'palette' },
            ]}
          />
        </SetRow>
        {background !== 'glow' ? (
          <SetRow label={t('set.bg.colour')}>
            <SolidSwatches
              label={t('set.bg.colour')}
              options={SOLIDS[dark ? 'dark' : 'light']}
              value={shown}
              onChange={pickBackground}
            />
          </SetRow>
        ) : null}
        {/* reduced motion and transparency (I12, C29) */}
        <SetRow label={t('set.motion')} sub={t('set.motion.sub')}>
          <Seg<MotionPref>
            label={t('set.motion')}
            value={motion}
            onChange={(v) => {
              setMotionPref(v);
              setMotion(v);
            }}
            options={[
              { value: 'system', label: t('set.motion.system') },
              { value: 'reduce', label: t('set.motion.on') },
              { value: 'full', label: t('set.motion.off') },
            ]}
          />
        </SetRow>
        <SetRow label={t('set.glass')} sub={t('set.glass.sub')}>
          <Seg<GlassPref>
            label={t('set.glass')}
            value={glass}
            onChange={(v) => {
              setGlassPref(v);
              setGlass(v);
            }}
            options={[
              { value: 'full', label: t('set.glass.full') },
              { value: 'flat', label: t('set.glass.flat') },
            ]}
          />
        </SetRow>
      </SetGroup>
    </>
  );
}

type Permission = NotificationPermission | 'unsupported';

const readPermission = (): Permission =>
  typeof Notification === 'undefined' ? 'unsupported' : Notification.permission;

/**
 * The browser's notification permission, read again when the window gets
 * focus (it may have been changed in the site settings meanwhile).
 */
function useNotificationPermission(): [Permission, () => Promise<Permission>] {
  const [perm, setPerm] = useState<Permission>(readPermission);
  useEffect(() => {
    const again = () => setPerm(readPermission());
    window.addEventListener('focus', again);
    document.addEventListener('visibilitychange', again);
    return () => {
      window.removeEventListener('focus', again);
      document.removeEventListener('visibilitychange', again);
    };
  }, []);
  const ask = useCallback(async (): Promise<Permission> => {
    if (typeof Notification === 'undefined') return 'unsupported';
    let r: Permission;
    try {
      r = await Notification.requestPermission();
    } catch {
      r = readPermission();
    }
    setPerm(r);
    return r;
  }, []);
  return [perm, ask];
}

/**
 * Desktop notifications. On the web the switch is the account's choice, and
 * the browser must allow them too: until it has been asked, the screen says
 * so and offers to ask (live bug 2026-10-28: the switch was on by default,
 * nothing ever asked, and no notification ever appeared). A save that fails
 * puts the switch back and says why.
 */
export function NotificationsSection() {
  const { t } = useT();
  const on = useData((d) => d.settings?.desktop_notifications ?? false);
  const web = !desktopBridge();
  const [perm, ask] = useNotificationPermission();
  const [busy, setBusy] = useState(false);
  const save = async (v: boolean) => {
    const put = (x: boolean) =>
      updateData((d) =>
        d.settings ? { ...d, settings: { ...d.settings, desktop_notifications: x } } : d,
      );
    put(v);
    try {
      const s = await api.me.patchSettings({ desktop_notifications: v });
      updateData((d) => ({ ...d, settings: s }));
    } catch (e) {
      put(!v);
      failToast(e);
    }
  };
  const allow = async (): Promise<boolean> => {
    const r = await ask();
    if (r === 'granted') return true;
    const why =
      r === 'unsupported'
        ? t('set.notif.unsupported')
        : r === 'denied'
          ? t('ns.set.notifBlocked')
          : t('set.notif.notAllowed');
    toast(why, { tone: 'warn', icon: 'bell-off' });
    return false;
  };
  return (
    <>
      <h2>{t('ns.set.notifications')}</h2>
      <SetGroup>
        <SetRow
          label={t('ns.set.desktopNotif')}
          sub={web ? t('ns.set.webNotifSub') : t('ns.set.desktopNotifSub')}
        >
          <Switch
            checked={on}
            disabled={busy}
            label={t('ns.set.desktopNotif')}
            onChange={async (v) => {
              setBusy(true);
              try {
                if (v && web && perm !== 'granted' && !(await allow())) return;
                await save(v);
              } finally {
                setBusy(false);
              }
            }}
          />
        </SetRow>
        {web && on && perm === 'default' ? (
          <div className="nx-set-row ns-notif-ask" role="status">
            <div className="nx-set-rt">
              <div className="s">{t('set.notif.ask')}</div>
            </div>
            <div className="nx-set-ctl">
              <Btn size="sm" kind="primary" onClick={() => void allow()}>
                {t('set.notif.allow')}
              </Btn>
            </div>
          </div>
        ) : null}
        {web && perm === 'denied' ? (
          <p className="nx-set-note">
            <Icon name="bell-off" />
            <span>{t('ns.set.notifBlocked')}</span>
          </p>
        ) : null}
        {web && perm === 'unsupported' ? (
          <p className="nx-set-note">
            <Icon name="info" />
            <span>{t('set.notif.unsupported')}</span>
          </p>
        ) : null}
      </SetGroup>
    </>
  );
}

export function AboutSection() {
  const { t } = useT();
  return (
    <>
      <h2>{t('set.about')}</h2>
      <div className="nx-about nx-card">
        <img src={markUrl} alt="" width={56} height={56} />
        <div>
          <div className="nx-about-wm" dir="ltr">
            <span className="nx-wm">NEOSOCIAL</span>
            <span className="nx-wm-tag">ALPHA</span>
          </div>
          <p>{t('set.about.version', { v: desktopBridge()?.appVersion ?? '0.1.0' })}</p>
          <UpdateStatusLine />
        </div>
      </div>
      <ul className="nx-about-notes">
        <li>{t('set.about.fonts')}</li>
        <li>{t('ns.about.server', { origin: serverOrigin() || location.origin })}</li>
        <li>{t('ns.about.noForeign')}</li>
      </ul>
    </>
  );
}
